import { Canvas, CanvasObject, Connection, DraggableObject } from '.';

export type Alignment = 'start' | 'end';
export type Side = 'top' | 'right' | 'bottom' | 'left';
export type AlignedPlacement = `${Side}-${Alignment}`;
export type Placement = Side | AlignedPlacement;

export interface ICoordinates {
	x: number;
	y: number;
}

export interface ICanvasOptions {
	containerElement: HTMLElement;
	id: string;
	connectionDefaults?: IConnectionOptions;
	elementClasses: string[];
	enableUndoRedo?: boolean;
	invertScrollDirection?: boolean;
}

export interface IConnection {
	source: HTMLElement;
	target: HTMLElement;
}

export interface IBaseCanvasObjectCreateOptions {
	element: HTMLElement;
	canvas: Canvas;
	id: string;
}

export interface IAbsoluteCanvasObjectCreateOptions extends IBaseCanvasObjectCreateOptions {
	coordinates: ICoordinates;
}

export interface ICanvasObjectCreateOptions extends IAbsoluteCanvasObjectCreateOptions {
	coordinates: ICoordinates;
	selectable?: boolean;
	deletable?: boolean;
}

export interface IUpdateNonDraggableConfig {
	coordinates?: ICoordinates;
	element?: HTMLElement;
	deleted?: boolean;
	newConnections?: IConnectionValue[];
	deletedConnections?: IConnectionValue[];
}

export interface IUpdateDraggableConfig {
	coordinates?: ICoordinates;
	element?: HTMLElement;
	deleted?: boolean;
	newConnections?: IConnectionValue[];
	deletedConnections?: IConnectionValue[];
}

export interface INonDraggableConfig extends IUpdateNonDraggableConfig {
	coordinates: ICoordinates;
	element: HTMLElement;
	selected: boolean;
	overlaps: OverlapReport;
}

export interface IDraggableConfig extends IUpdateDraggableConfig {
	coordinates: ICoordinates;
	element: HTMLElement;
	selected: boolean;
	overlaps: OverlapReport;
}

export interface IConnectionValueParent {
	objectId: string;
	objectType: 'draggable' | 'non-draggable';
}

export interface IConnectionOptions {
	size?: number;
	color?: string;
	dash?: boolean | { animation: boolean };
	complementaryColor?: string;
	hoverColor?: string;
	selectedColor?: string;
	onClick?: (connection: Connection) => any;
	onUnselect?: (connection: Connection) => HTMLElement | void;
	onEnable?: (connection: Connection) => void;
	onDisable?: (connection: Connection) => void;
	classes?: string[];
}

export interface IConnectionValue {
	id: string;
	startId: string;
	endId: string;
	instance: Connection | null;
	deleted: boolean;
	initialOptions: IConnectionOptions;
	sourceParentObject: IConnectionValueParent;
	targetParentObject: IConnectionValueParent;
	selected: boolean;
	type: 'connection';
}

export interface IDraggableUndoableValue {
	id: string;
	selected: boolean;
	x: number;
	y: number;
	instance: DraggableObject | CanvasObject | null;
	connections: IConnectionValue[];
	deleted: boolean;
	selectable: boolean;
	deletable: boolean;
	disabled: boolean;
	type: 'draggable' | 'non-draggable';
}

export interface IDraggableValue extends IDraggableUndoableValue {
	element: HTMLElement;
}

export type DraggableOnUpdateCallback = (
	config: IDraggableConfig
) => IDraggableConfig;

export type NonDraggableOnUpdateCallback = (
	config: INonDraggableConfig
) => INonDraggableConfig;

export type DraggableBeforeUpdateCallback = (
	config: IDraggableConfig
) => IUpdateDraggableConfig;

export type DraggableBeforeDeleteConnectionCallback = (
	connection: Connection,
	config: {
		// Whether the connection is being delete alone or as part of a large deletion of objects/connections
		batchDelete: boolean;
	}
) => { blockDelete?: boolean; skipTargetCheck?: boolean } | void;

export type NonDraggableBeforeUpdateCallback = (
	config: INonDraggableConfig
) => IUpdateNonDraggableConfig;

export interface IOverlapDefinition {
	overlappingObject: DraggableObject;
	connection: Connection;
	nextValidCoordinates: ICoordinates;
}

export type Overlaps = IOverlapDefinition[];

export type OverlapReport = {
	coords: IOverlapDefinition | null;
	altCoords: IOverlapDefinition | null;
}[];

export interface IArea {
	// Left
	left: number;
	// Top
	top: number;
	// Right
	right: number;
	// Bottom
	bottom: number;
}

export const MIN_ARROW_AREA_PX_WIDTH = 200;
